import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import styled from '@emotion/styled'
import DownloadModal from 'components/download-modal'
import RouteLogo from 'components/route-logo'
import Pill from 'components/pill'
import Button from 'components/button'
import AButton from 'components/a-button'
import Accent from 'components/accent'
import PlayButton from 'assets/play-button'
import Location from 'assets/location'
import Send from 'assets/send'
import api from 'api'
import isMobileDevice from 'utils/isMobileDevice'
import StoryCard from './story-card'

const Background = styled.div`
  background: linear-gradient(180deg, #023646 0%, #05222a 66.84%);
  height: 100vh;
  text-align: center;
  overflow: hidden;
`

const ImageWrapper = styled.div`
  margin: auto;
`

const TextContainer = styled.div`
  padding-top: 42px;
  width: 100%;
  padding-bottom: 42px;
`

const Text = styled.p`
  margin: 0 auto;
  width: 440px;
  color: white;
  font-weight: 700;
  font-size: 34px;
  line-height: 36px;
  @media (max-width: 600px) {
    width: auto;
  }
`

const Phone = styled.div`
  margin: 0 auto;
  margin-top: 42px;
  border: 12px solid rgba(69, 221, 255, 0.6);
  border-radius: 58px;
  height: 874px;
  width: 400px;
  box-sizing: border-box;
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1),
    rgba(0, 0, 0, 1),
    rgba(0, 0, 0, 0.3),
    rgba(0, 0, 0, 0)
  );
  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1),
    rgba(0, 0, 0, 1),
    rgba(0, 0, 0, 0.3),
    rgba(0, 0, 0, 0)
  );
  margin-bottom: 48px;
  @media (max-width: 448px) {
    width: auto;
  }
  cursor: pointer;
`

const Scroll = styled.div`
  overflow-y: auto;
  height: 100%;
  padding: 24px;
`

const Screen = styled.div`
  cursor: pointer;
  border-radius: 44px;
  overflow: hidden;
  height: 850px;
  background-color: black;
`

const HeaderImage = styled.div`
  width: 100%;
  background-image: url(${({ url }) => url});
  background-position: center;
  background-size: 100%;
  background-repeat: no-repeat;
  height: 500px;
  position: relative;
`

const VideoTitle = styled.div`
  position: absolute;
  display: block;
  text-align: left;
  bottom: 0;
  width: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
`

const Padding = styled.div`
  padding: 18px;
`

const VideoTitleText = styled.p`
  color: white;
  font-weight: bold;
  font-size: 34px;
  margin: 0;
`

const Play = styled(PlayButton)`
  cursor: pointer;
`

const FlexInline = styled.div`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
`

const Circle = styled.div`
  position: relative;
  top: 4px;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  height: 36px;
  padding: 16px 16px 0px 16px;
`

const MobileButtonWrap = styled.div`
  display: flex;
  justify-content: space-around;
`

const Story = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const { storyId } = useParams()
  const history = useHistory()
  const [story, setStory] = useState()
  const [oneLink, setOneLink] = useState()
  const isMobile = useMemo(() => isMobileDevice())

  useEffect(() => {
    const func = async () => {
      if (isMobile) {
        const link = await api.share.getOneLink('story', storyId)
        setOneLink(link)
      }
    }

    func()
  }, [setOneLink, isMobile])

  useEffect(async () => {
    try {
      setStory(await api.story.getStoryById(storyId))
    } catch (e) {
      history.replace('/story')
    }
  }, [])

  const headerImage = story?.header_content.records[0].image_map.primary_image.url

  const stories = story?.content.records.map((mappedStory) => ({
    ...mappedStory.text_map,
    primary_text: mappedStory.text_map.primary_text,
    orientation:
      mappedStory.image_map?.primary_image.size.width
      > mappedStory.image_map?.primary_image.size.height
        ? 'landscape'
        : 'portrait',
    secondary_text: mappedStory.text_map.secondary_text,
    image: mappedStory.image_map?.primary_image.url,
    id: mappedStory.id,
  }))

  return (
    <>
      <Background>
        <Scroll>
          <ImageWrapper>
            <RouteLogo textColor="white" />
          </ImageWrapper>
          <TextContainer>
            <Text>
              <Accent>
                Discover
                {' '}
              </Accent>
              your new Summer essentials
            </Text>
          </TextContainer>
          <MobileButtonWrap>
            {isMobile && oneLink ? (
              <AButton href={oneLink}>Open In App</AButton>
            ) : (
              <Button onClick={() => setModalOpen(true)}>Open In App</Button>
            )}
          </MobileButtonWrap>
          <Phone>
            <Screen
              onClick={() => {
                if (isMobile && oneLink) {
                  window.location.href = oneLink
                } else { setModalOpen(true) }
              }}

            >
              <HeaderImage url={headerImage}>
                <div style={{ height: '42%' }} />
                <Play />
                <VideoTitle>
                  <Padding>
                    <div>
                      {story?.location && (
                        <Pill size="small">
                          <>
                            <Location />
                            {` ${story.location}`}
                          </>
                        </Pill>
                      )}
                    </div>
                    <FlexInline>
                      <VideoTitleText>{story?.title}</VideoTitleText>
                      <Circle>
                        <Send />
                      </Circle>
                    </FlexInline>
                  </Padding>
                </VideoTitle>
              </HeaderImage>
              {stories?.map((st) => (
                <StoryCard
                  orientation={st.orientation}
                  text={st.primary_text}
                  image={st.image}
                  key={st.id}
                />
              ))}
            </Screen>
          </Phone>
        </Scroll>
      </Background>
      <DownloadModal
        isOpen={modalOpen}
        close={() => setModalOpen(false)}
        contentLabel="Send link to download app"
      />
    </>
  )
}

export default Story
